import { Check, X, CircleDot, AlertTriangle, BookOpen, RotateCcw, ChevronDown } from 'lucide-react';

// Étiquette candidat des verdicts (on masque le jargon bonus/essentiel)
export const QRC_VERDICT_META = {
    present: { label: 'Bien traité', cls: 'ok', Icon: Check },
    partiel: { label: 'Partiellement', cls: 'mid', Icon: CircleDot },
    absent: { label: 'Manquant', cls: 'ko', Icon: X },
    contredit: { label: 'Contredit', cls: 'contradicted', Icon: AlertTriangle },
};

/**
 * Titre court d'un attendu pour le candidat — miroir de QrcJudge::titreFor côté PHP,
 * pour les verdicts enregistrés avant que `titre` existe. Le libellé complet porte
 * les règles de lecture du juge (« deux = partiel ») : il reste dans le détail.
 */
export function shortTitle(v) {
    const t = (v?.titre || '').trim();
    if (t) return t.slice(0, 60);
    const label = (v?.label || '').trim();
    const head = label.split(/\s+[:—]\s+/)[0] || label;
    if (head !== label && head.length <= 48) return head;
    if (v?.bonus) return 'Pour aller plus loin';
    if (label.length <= 48) return label;
    const cut = label.slice(0, 44);
    const sp = cut.lastIndexOf(' ');
    return (sp > 20 ? cut.slice(0, sp) : cut).replace(/[ ,;(]+$/, '') + '…';
}

/**
 * Verdict candidat d'une QRC corrigée : score, acquis/à retravailler, checklist
 * des attendus (titre court, détail dépliable : libellé complet + justification),
 * réponse attendue, alertes de blocage. Partagé player + audit + vitrine.
 *
 * Ce que le chiffre promet (revue Astra du 29/09/2026) : un gros pourcentage suivi
 * de « Acquis » et d'un seuil se lit comme une note de concours, et le candidat
 * n'invente pas cette lecture, l'affichage l'y invite. Le chiffre est en réalité la
 * couverture pondérée des critères de fond. On le dit sous le score, on liste ce
 * qu'il reste à compléter, et on rappelle que la forme n'entre pas en compte
 * (règle 2 du juge : il évalue le fond, jamais l'orthographe ni le style).
 */
export default function QrcVerdictView({ verdict, onReplay, replayLabel = 'Réessayer' }) {
    const passed = verdict.passed === 1 || verdict.passed === true;
    // Le nombre en tête est le SOCLE (celui qui décide l'acquisition face au seuil).
    // Le score de couverture (socle + bonus) n'est montré qu'en complément, pour
    // ne pas afficher « 80 % — à retravailler — réussite dès 70 % ».
    const core = verdict.score_core_pct ?? verdict.score_pct;
    // Les points « pour aller plus loin » ne sont PAS dans le score : ils s'ajoutaient
    // par-dessus, et la page affichait donc deux pourcentages rivaux, par exemple
    // « 57 % — à retravailler — réussite dès 70 % » puis « couverture totale 71 % ».
    // On lisait 71 face à un seuil de 70 et on se croyait reçu. Un seul nombre décide
    // désormais ; les bonus obtenus sont nommés, sans chiffre concurrent.
    const bonusObtenus = (verdict.verdicts || []).filter(v => v.bonus && v.verdict === 'present').length;
    // Masque les bonus non obtenus (un bonus absent ne « manque » pas)
    const shown = (verdict.verdicts || []).filter(v => !(v.bonus && v.verdict !== 'present'));
    // Ce qu'il reste à compléter : les critères de fond non acquis, nommés.
    // Les bonus en sont exclus, ils ne manquent jamais.
    const aCompleter = (verdict.verdicts || [])
        .filter(v => !v.bonus && (v.verdict === 'absent' || v.verdict === 'partiel'))
        .map(shortTitle);
    return (
        <div className={`qrcp-verdict ${passed ? 'is-pass' : 'is-fail'}`}>
            <div className="qrcp-verdict-head">
                <span className="qrcp-score">{core}<small> %</small></span>
                <span className={`qrcp-pill ${passed ? 'ok' : 'ko'}`}>
                    {passed ? <><Check size={14} /> Acquis</> : <><X size={14} /> À retravailler</>}
                </span>
                <span className="qrcp-seuil">Réussite dès {verdict.seuil} %</span>
            </div>
            <p className="qrcp-mesure">
                Couverture pondérée des critères de fond, pas une note de concours.
                <span> Le style et la qualité de rédaction ne sont pas évalués.</span>
            </p>
            {aCompleter.length > 0 && (
                <p className="qrcp-acompleter"><strong>À compléter :</strong> {aCompleter.join(' · ')}</p>
            )}
            {bonusObtenus > 0 && (
                <p className="qrcp-bonus">
                    Tu as traité {bonusObtenus === 1 ? 'un point' : `${bonusObtenus} points`} « pour aller plus loin ».
                    {' '}Cela valorise ta copie sans entrer dans le score, qui ne mesure que les critères de fond.
                </p>
            )}

            {verdict.blocked && verdict.blocking?.length > 0 && (
                <div className="qrcp-alert">
                    <AlertTriangle size={16} />
                    <div><strong>Point à ne jamais faire.</strong> {verdict.blocking.join(' · ')}</div>
                </div>
            )}
            {verdict.essential_contradiction && !verdict.blocked && (
                <div className="qrcp-alert">
                    <AlertTriangle size={16} />
                    <div><strong>Un point essentiel est contredit.</strong></div>
                </div>
            )}

            <div className="qrcp-check-title">Ce que la correction attendait</div>
            <ul className="qrcp-check">
                {shown.map((v, i) => {
                    const meta = QRC_VERDICT_META[v.verdict] || QRC_VERDICT_META.absent;
                    const Ic = meta.Icon;
                    const title = shortTitle(v);
                    const fullLabel = (v.label || '').trim();
                    const showLabel = fullLabel && fullLabel !== title;
                    return (
                        <li key={i} className="qrcp-check-item">
                            <details className="qrcp-check-det">
                                <summary>
                                    <span className={`qrcp-check-ic ${meta.cls}`}><Ic size={13} /></span>
                                    <span className="qrcp-check-t">{title}</span>
                                    <span className={`qrcp-check-state is-${meta.cls}`}>{meta.label}</span>
                                    <ChevronDown size={14} className="qrcp-check-arrow" />
                                </summary>
                                <div className="qrcp-check-body">
                                    {showLabel && <p className="qrcp-check-label">{fullLabel}</p>}
                                    {v.justification && <p className="qrcp-check-just">{v.justification}</p>}
                                </div>
                            </details>
                        </li>
                    );
                })}
            </ul>

            {verdict.commentaire && <p className="qrcp-comment">💬 {verdict.commentaire}</p>}
            {verdict.reponse_modele && (
                <details className="qrcp-model-det">
                    <summary>
                        <BookOpen size={14} />
                        <span>Voir une réponse qui répond à tous les attendus</span>
                        <ChevronDown size={14} className="qrcp-model-arrow" />
                    </summary>
                    <div className="qrcp-model">
                        <p>{verdict.reponse_modele}</p>
                    </div>
                </details>
            )}

            {onReplay && (
                <button type="button" className="qrcp-replay" onClick={onReplay}><RotateCcw size={13} /> {replayLabel}</button>
            )}
        </div>
    );
}
